import { Button as MuiButton, Card, Link, Stack, Typography } from '@mui/material';

import ClaimScreen, { ClaimBar } from '@/james-playground/aa-family-claim/ClaimScreen';
import { AREAS, OWNER, say } from '@/james-playground/aa-family-claim/claim/asks';
import { voiceOf, type ClaimState } from '@/james-playground/aa-family-claim/claim/claim';
import { SEND } from '@/james-playground/aa-family-claim/claim/claim.content';
import { skipped } from '@/james-playground/aa-family-claim/claim/engine';
import { labelOf } from '@/james-playground/aa-family-claim/claim/labels';

/**
 * Send to the Care Adviser (spec 11): what goes with it, everything set aside
 * to talk through, and how the form then reaches DWP. The last step is the
 * swappable one while the submission route is undecided.
 */
const SendScreen = ({
  claim,
  onBack,
  onSend,
  onDownload,
}: {
  claim: ClaimState;
  onBack: () => void;
  onSend: () => void;
  onDownload: () => void;
}) => {
  const v = voiceOf(claim);
  const aside = skipped(claim.facts);
  const groups = AREAS.map((area) => ({
    label: say(area.label, v),
    items: aside.filter((e) => OWNER[e.id]?.area === area.id),
  })).filter((g) => g.items.length);

  return (
    <ClaimScreen
      bar={<ClaimBar onBack={onBack} />}
      title={SEND.title}
      description={SEND.description}
      footer={
        <MuiButton variant='contained' size='large' fullWidth onClick={onSend}>
          {SEND.send}
        </MuiButton>
      }
    >
      <Stack spacing={1.5}>
        <Typography component='h2' variant='h4'>
          {SEND.talkThrough}
        </Typography>
        <Card sx={{ p: 2 }}>
          {groups.length ? (
            <Stack spacing={1.5}>
              {groups.map((g) => (
                <Stack key={g.label} spacing={0.5}>
                  <Typography variant='overline'>{g.label}</Typography>
                  <Stack component='ul' sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                    {g.items.map((e) => (
                      <Typography key={e.id} component='li' variant='body2'>
                        {labelOf(e.id, v)}
                      </Typography>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant='body2'>{SEND.nothingSkipped}</Typography>
          )}
        </Card>
      </Stack>
      <Stack spacing={1}>
        <Typography component='h2' variant='h4'>
          {SEND.howItGoes}
        </Typography>
        <Typography variant='body1'>{say(SEND.route, v)}</Typography>
        <Link component='button' type='button' variant='body2' onClick={onDownload} sx={{ alignSelf: 'flex-start' }}>
          {SEND.pdf}
        </Link>
      </Stack>
    </ClaimScreen>
  );
};

export default SendScreen;
